// Importaciones necesarias desde React y otras dependencias
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GptMessage, // Componente para mostrar mensajes generados por GPT
  MyMessage, // Componente para mostrar mensajes del usuario
  TypingLoader, // Componente de animación mientras se carga una respuesta
  TextMessageBox, // Componente de la caja de texto donde el usuario escribe
} from "../../../components/NewAssistance";
import {
  createThreadUseCase, // Caso de uso para crear un nuevo hilo de conversación
  postQuestionUseCase, // Caso de uso para enviar una pregunta y obtener una respuesta
  AssistanceApi, // Clase para interactuar con la API de asistencia
} from "../../../api";
import { useAuth } from "../../../hooks"; // Hook personalizado para manejar autenticación

// Crear una instancia del controlador de la API de asistencia
const assistanceApiController = new AssistanceApi();

// Componente principal para la nueva asistencia
export const NewAssistance = () => {
  // Obtener el ID del asistente desde la URL
  const { assistantId } = useParams();

  // Definición de estados
  const [isLoading, setIsLoading] = useState(false); // Estado de carga
  const [messages, setMessages] = useState([]); // Estado para almacenar mensajes
  const [threadId, setThreadId] = useState(null); // Estado para almacenar el ID del hilo de conversación
  const { accessToken } = useAuth(); // Obtener el token de acceso desde la autenticación
  const [imageAvatar, setImageAvatar] = useState(null); // Estado para almacenar la ruta del avatar del asistente

  // Efecto secundario que se ejecuta cuando se monta el componente o cuando cambian assistantId o accessToken
  useEffect(() => {
    // Función asincrónica para obtener datos y configurar el hilo de conversación
    const fetchData = async () => {
      if (assistantId && accessToken) {
        try {
          // Intentar obtener la imagen del avatar del almacenamiento local
          let storedImagePath = localStorage.getItem(`imageAvatar`);

          if (storedImagePath) {
            // Si ya existe, establecerla en el estado
            setImageAvatar(storedImagePath);
          } else {
            // Si no existe, obtenerla de la API y guardarla en el almacenamiento local
            const imagePath =
              await assistanceApiController.getMiniature(assistantId);
            setImageAvatar(imagePath);
            localStorage.setItem(`imageAvatar`, imagePath);
          }

          // Crear un nuevo hilo de conversación y almacenar su ID
          const threadId = await createThreadUseCase(accessToken, assistantId);
          setThreadId(threadId);
          localStorage.setItem("threadId", threadId);
        } catch (error) {
          console.error("Error en las llamadas asincrónicas:", error);
        }
      } else {
        console.error(
          "No se proporcionó un ID de asistente en la URL o falta el accessToken",
        );
      }
    };

    // Llamar a la función asincrónica para obtener los datos
    fetchData();
  }, [assistantId, accessToken]);

  // Función para manejar el envío de mensajes
  const handlePost = async (text) => {
    // Verificar si el ID del hilo está disponible
    if (!threadId) return;

    // Establecer estado de carga y agregar el mensaje del usuario a la lista de mensajes
    setIsLoading(true);
    setMessages((prev) => [...prev, { text: text, isGpt: false }]);

    try {
      // Enviar la pregunta y obtener las respuestas
      const replies = await postQuestionUseCase(
        accessToken,
        threadId,
        text,
        assistantId,
      );
      setIsLoading(false);

      let lastAssistantMessage = null;

      // Filtrar la última respuesta del asistente
      for (const reply of replies) {
        if (reply.role === "assistant") {
          lastAssistantMessage = reply.content[reply.content.length - 1];
        }
      }

      // Si hay una respuesta del asistente, agregarla a la lista de mensajes
      if (lastAssistantMessage) {
        setMessages((prev) => [
          ...prev,
          { text: lastAssistantMessage, isGpt: true },
        ]);
      }
    } catch (error) {
      console.error("Error al enviar la pregunta", error);
      setIsLoading(false);
    }
  };

  // Retornar la interfaz del componente
  return (
    <div className="chat-container">
      <div className="chat-messages">
        <div className="grid grid-cols-12 gap-y-2">
          <GptMessage
            text="Saludos, soy el tutor de este curso. ¿En qué puedo ayudarte?"
            imageAvatar={imageAvatar}
          />

          {/* Renderizar los mensajes en la interfaz */}
          {messages.map((message, index) =>
            message.isGpt ? (
              <GptMessage
                key={index}
                text={message.text}
                imageAvatar={imageAvatar}
              />
            ) : (
              <MyMessage key={index} text={message.text} />
            ),
          )}

          {/* Mostrar la animación de carga si se está esperando una respuesta */}
          {isLoading && (
            <div className="col-start-1 col-end-12 fade-in">
              <TypingLoader />
            </div>
          )}
        </div>
      </div>

      {/* Componente de la caja de texto para enviar mensajes */}
      <TextMessageBox
        onSendMessage={handlePost}
        placeholder="Escribe aquí lo que deseas"
        disableCorrections
      />
    </div>
  );
};
